<template>
  <div class="vcl-checkbox">
    <label v-if="$slots.label && labelFirst" :for="_uid+'-checkbox'" :class="['vcl-checkbox__label', { 'disabled': disabled }]" :style="labelStyles">
      <slot name="label" />
    </label>
    <div
      class="vcl-checkbox__box"
      :tabindex="styles.tabindex || tabIndex"
      :style="boxStyles"
      @focus="focusGained"
      @blur="focusLost"
      @keydown="handleKeydown"
    >
      <input
        :id="_uid+'-checkbox'"
        ref="checkbox"
        class="vcl-checkbox__input"
        type="checkbox"
        :checked="value"
        :disabled="disabled"
        v-bind="attrs"
        @change="$emit('input', $event.target.checked)"
      />
      <label :for="_uid+'-checkbox'" class="vcl-checkbox__checkbox" :style="checkboxStyles">
        <VpIcon class="vcl-checkbox__checkmark" :icon="icon" />
      </label>
    </div>
    <label v-if="$slots.label && !labelFirst" :for="_uid+'-checkbox'" :class="['vcl-checkbox__label', { 'disabled': disabled }]" :style="labelStyles">
      <slot name="label" />
    </label>
  </div>
</template>

<script>
import VpIcon from '../vp-icon/VpIcon.vue';

export default {
  name: 'VpCheckbox',
  components: {
    VpIcon,
  },
  props: {
    id: {
      type: String,
      default: '',
    },
    value: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: 'white-check',
    },
    labelFirst: {
      type: Boolean,
      default: false,
    },
    styles: {
      type: Object,
      default() {
        return {};
      },
    }
  },
  emits: ['input'],
  data() {
    return {
      hasFocus: false,
    };
  },
  computed: {
    boxStyles() {
      // the box container needs to be 6px wider than the actual checkbox so that we can display
      // a border when focused
      const size = (this.styles.size ? `${Number(this.styles.size) + 6}px` : '28px');
      return {
        width: size,
        height: size,
      };
    },
    checkboxStyles() {
      return {
        '--checkbox-size': this.styles.size ? `${Number(this.styles.size)}px` : '22px',
        '--checkbox-color': this.styles.color || '#016699',
        '--checkbox-border-radius': this.styles.borderRadius || '4px',
        '--checkbox-checkmark-size': this.styles.size ? `${Number(this.styles.size) * 0.65}px` : '15px',
        '--checkbox-checkmark-color': this.styles.checkmarkColor || '#fff',
        '--checkbox-disabled-color': this.styles.disabledColor || '#AFAFAF',
      };
    },
    labelStyles() {
      return {
        '--label-padding-left': this.styles.size ? `${Number(this.styles.size)}px` : '22px',
        '--label-disabled-color': this.styles.labelDisabledColor || '#DBDBDB',
      };
    },
    tabIndex() {
      return (this.disabled) ? -1 : 0;
    },
    attrs() {
      const { class: classAttr, style, ...attrs } = this.$attrs;
      return attrs;
    },
  },
  methods: {
    focusGained() {
      this.hasFocus = true;
    },
    focusLost() {
      this.hasFocus = false;
    },
    handleKeydown(evt) {
      if ((evt.key === 'Space') || (evt.key === ' ')) {
        this.$refs.checkbox.checked = !this.$refs.checkbox.checked;
        this.$emit('input', this.$refs.checkbox.checked);
        evt.stopPropagation();
        evt.preventDefault();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
* {
  box-sizing: border-box;
}
.vcl-checkbox {
  $self: &;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  user-select: none;
  &__box, &__checkbox {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &__box {
    margin: 0;
    padding: .5rem;
    cursor: pointer;
    position: relative;
    .vcl-checkbox__input {
      visibility: hidden;
      margin: 0 !important;
      padding: 0 !important;
      position: absolute;
      & + label.vcl-checkbox__checkbox {
        margin: 0 !important;
        padding: 0 !important;
        content: '';
        min-width: var(--checkbox-size);
        min-height: var(--checkbox-size);
        border-radius: var(--checkbox-border-radius);
        background-color: #fff;
        border: 1px solid #AFAFAF;
        cursor: pointer;

        #{ $self }__checkmark {
          margin: 0;
          padding: 0;
          font-size: var(--checkbox-checkmark-size);
          color: var(--checkbox-checkmark-color);
          outline:0;
        }
      }
      &:checked {
        & + label.vcl-checkbox__checkbox {
          background-color: #016699;
          background-color: var(--checkbox-color);
          &:after {
            opacity: 1;
          }
        }
      }
      &:disabled {
        &:checked  + label.vcl-checkbox__checkbox {
          cursor: not-allowed;
          background-color: #AFAFAF;
          background-color: var(--checkbox-disabled-color);
        }
      }
      &:focus:not(:focus-visible) {
        outline: none;
      }
    }
  }
  &__label {
    user-select: none;
    padding: 0;
    margin: 0 0 0 5px;
    color: #4F4F4F;
    cursor: pointer;
    display: inline-block !important;
    &.disabled {
      color: #DBDBDB;
      color: var(--label-disabled-color);
      cursor: not-allowed;
    }
  }
}
</style>
